import axios from 'axios'

import {
  readBrowserNotifications,
  writeBrowserNotifications,
} from 'Pods/ThreeDStudio/Services/notificationPreferencesStorage'

import { threeDStudioStore } from '../index'

const EMAIL_PREFERENCE_PATH = '/api/internal/generator/notification-settings'

/**
 * Seeds the browser-push toggle from localStorage. Called after mount rather than from the slice
 * default so nothing reads browser storage during the SSR render of this fast page. No-ops when
 * the stored value already matches, keeping repeat calls from every mounted surface free.
 */
export const hydrateBrowserNotifications = () => {
  const stored = readBrowserNotifications()
  const current = threeDStudioStore.getState().notificationPreferences

  if (stored === current.browserNotifications) return

  threeDStudioStore.setState({
    notificationPreferences: { ...current, browserNotifications: stored },
  })
}

/**
 * Merges a toggle change into the shared preferences. Both surfaces (first-generation popover,
 * Settings modal) are mounted at once, so they patch one store entry: with a copy per surface the
 * longer-lived popover wrote its stale value back over whatever the modal had just persisted.
 * Only browserNotifications is persisted locally; emailWhenReady goes to the server.
 * @param {Object} patch - Partial preferences (e.g. { emailWhenReady: true })
 */
export const patchNotificationPreferences = (patch) => {
  const next = { ...threeDStudioStore.getState().notificationPreferences, ...patch }

  if ('browserNotifications' in patch) writeBrowserNotifications(next.browserNotifications)

  threeDStudioStore.setState({ notificationPreferences: next })
}

/**
 * Applies the email toggle optimistically and persists it to User#email_settings, reverting in
 * place if the write fails. Lives here rather than in a component hook so closing the Settings
 * modal mid-request cannot abort it: useApiPost aborts on unmount and swallows the cancellation
 * without reporting an error, which would leave the store showing a value the server never got.
 * @param {boolean} enabled
 */
export const saveEmailWhenReady = (enabled) => {
  const previous = threeDStudioStore.getState().notificationPreferences.emailWhenReady

  patchNotificationPreferences({ emailWhenReady: enabled })

  const metaToken = document.getElementsByName('csrf-token')[0]

  return axios.post(
    EMAIL_PREFERENCE_PATH,
    { email_when_ready: enabled },
    {
      headers: { 'X-CSRF-Token': metaToken?.getAttribute('content') },
      withCredentials: true,
    }
  ).catch((error) => {
    // There is no error toast, so an unreported failure would be completely invisible.
    window.Sentry?.captureException(error, { extra: { path: EMAIL_PREFERENCE_PATH, enabled } })

    // Only roll back if this request still owns the displayed value. On a double-toggle the
    // second write has already landed, and reverting would discard the newer choice.
    if (threeDStudioStore.getState().notificationPreferences.emailWhenReady !== enabled) return

    patchNotificationPreferences({ emailWhenReady: previous })
  })
}
